import React from "react";
import { useNavigate } from "react-router-dom";
import {
  MdArticle,
  MdCalendarToday,
  MdChevronLeft,
  MdChevronRight,
  MdClose,
  MdRefresh,
  MdSearch,
  MdSchedule,
  MdTune,
} from "react-icons/md";
import Card from "components/card";
import ActionButton from "components/ui/ActionButton";
import StatusBadge from "components/ui/StatusBadge";
import { getSessionStatusTone } from "constants/colors";
import { useLanguage } from "context/LanguageContext";
import { classMgmtService } from "services/admin/classMgmtService";
import { reportService } from "services/admin/reportService";
import { teachingClassService } from "services/userTeaching/teachingClassService";
import { getCurrentBackendRole } from "utils/auth";
import { addDays, formatWeekRangeLabel, getWeekStart } from "utils/weekDate";
import type {
  SessionParticipant,
  SessionReport,
  SessionWithDetails,
} from "types/domain";
import ReportView from "views/admin/sessions/ReportView";

type Translate = (
  key: string,
  params?: Record<string, string | number>
) => string;

const STAFF_ROLES = ["teacher", "trainer"] as const;

// Only checked-in teacher/trainer participants are expected to file a report.
const isReportRequiredParticipant = (
  participant: SessionParticipant
): boolean =>
  (STAFF_ROLES as readonly string[]).includes(participant.role) &&
  Boolean(participant.checked_in_at);

const FEED_PAGE_SIZE = 100;

// CLOSED is shown as COMPLETED on purpose: both mean the session is over.
const statusBadgeKey = (status?: string) => {
  if (status === "ACTIVE") return "enum.sessionStatus.ACTIVE";
  if (status === "COMPLETED" || status === "CLOSED")
    return "enum.sessionStatus.COMPLETED";
  if (status === "CANCELLED") return "enum.sessionStatus.CANCELLED";
  return "enum.sessionStatus.SCHEDULED";
};

// Indexed by Date#getDay() (0 = Sunday).
const WEEKDAY_SHORT_KEYS = [
  "excelExport.weekday.sun",
  "excelExport.weekday.mon",
  "excelExport.weekday.tue",
  "excelExport.weekday.wed",
  "excelExport.weekday.thu",
  "excelExport.weekday.fri",
  "excelExport.weekday.sat",
];

const isCoordinateLike = (value?: string | null): boolean => {
  if (!value) return false;
  return /^\s*-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?\s*$/.test(value);
};

const getSessionLocationLabel = (
  session: SessionWithDetails,
  t: Translate
): string => {
  if (session.venue?.name?.trim()) return session.venue.name.trim();
  if (session.venue?.address?.trim()) return session.venue.address.trim();

  const fallback = session.location?.trim() ?? "";
  if (!fallback || isCoordinateLike(fallback)) {
    return t("reportsBoard.noLocation");
  }

  return fallback;
};

const formatSessionDateLabel = (
  startIso: string | null | undefined,
  t: Translate,
  locale: string
): string => {
  if (!startIso) return t("reportsBoard.noDate");
  const date = new Date(startIso);
  if (Number.isNaN(date.getTime())) return t("reportsBoard.noDate");
  return date.toLocaleDateString(locale, {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatSessionTimeRange = (
  startIso: string | null | undefined,
  endIso: string | null | undefined,
  t: Translate,
  locale: string
): string => {
  if (!startIso) return t("reportsBoard.noTime");
  const start = new Date(startIso).toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });
  if (!endIso) return start;
  const end = new Date(endIso).toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${start} - ${end}`;
};

const formatSessionDateCompact = (
  startIso: string | null | undefined,
  t: Translate
): string => {
  if (!startIso) return t("reportsBoard.noDate");
  const date = new Date(startIso);
  if (Number.isNaN(date.getTime())) return t("reportsBoard.noDate");
  const weekday = t(WEEKDAY_SHORT_KEYS[date.getDay()]);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${weekday} ${day}/${month}/${year}`;
};

const formatDateTime = (
  iso: string | null | undefined,
  t: Translate,
  locale: string
): string => {
  if (!iso) return t("reportsBoard.noSchedule");
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return t("reportsBoard.noSchedule");
  return date.toLocaleString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const SessionReportsBoard: React.FC<{ centerId?: string }> = ({ centerId }) => {
  const navigate = useNavigate();
  const { t, locale } = useLanguage();
  const backendRole = getCurrentBackendRole();
  const canViewAllClasses =
    backendRole === "MANAGER" || backendRole === "SYSTEM_ADMIN";
  const [sessions, setSessions] = React.useState<SessionWithDetails[]>([]);
  const [reportCountBySessionId, setReportCountBySessionId] = React.useState<
    Record<string, number>
  >({});
  const [selectedSessionId, setSelectedSessionId] = React.useState<
    string | null
  >(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState("");
  const [weekOffset, setWeekOffset] = React.useState(0);
  const [classFilter, setClassFilter] = React.useState("ALL");

  const weekStart = React.useMemo(
    () => addDays(getWeekStart(new Date()), weekOffset * 7),
    [weekOffset]
  );
  const weekEnd = React.useMemo(() => {
    const end = addDays(weekStart, 6);
    end.setHours(23, 59, 59, 999);
    return end;
  }, [weekStart]);
  const [selectedSessionReports, setSelectedSessionReports] = React.useState<
    SessionReport[]
  >([]);

  const handleSelectedSessionReportsLoaded = React.useCallback(
    (reports: SessionReport[]) => {
      setSelectedSessionReports(reports);
    },
    []
  );

  const loadSessions = React.useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // When a centerId is provided (System Admin acting-as-center-owner),
      // scope the session list to classes that belong to that center.
      const centerScopedClassIds = centerId
        ? new Set(
            (
              await classMgmtService.getAll({
                skip: 0,
                limit: 200,
                center_id: centerId,
              })
            ).items.map((item) => item.id)
          )
        : null;
      const assignedClasses = canViewAllClasses
        ? null
        : await teachingClassService.getMyAssignedClasses();
      const allowedClassIds = assignedClasses
        ? new Set(assignedClasses.map((item) => item.id))
        : centerScopedClassIds;

      let skip = 0;
      const items: SessionWithDetails[] = [];

      while (true) {
        const response = await classMgmtService.getSessionsFeed({
          skip,
          limit: FEED_PAGE_SIZE,
        });

        items.push(...response.items);

        if (
          response.items.length < FEED_PAGE_SIZE ||
          items.length >= response.total
        ) {
          break;
        }
        skip += FEED_PAGE_SIZE;
      }

      const sorted = [...items].sort((a, b) => {
        const left = a.start_time ? new Date(a.start_time).getTime() : 0;
        const right = b.start_time ? new Date(b.start_time).getTime() : 0;
        return right - left;
      });

      const scopedSessions = allowedClassIds
        ? sorted.filter(
            (session) =>
              session.class_id && allowedClassIds.has(session.class_id)
          )
        : sorted;

      setSessions(scopedSessions);
      setReportCountBySessionId({});
      setSelectedSessionId((prev) => {
        if (prev && scopedSessions.some((session) => session.id === prev)) {
          return prev;
        }
        return scopedSessions[0]?.id ?? null;
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "reportsBoard.loadFailed");
      setSessions([]);
      setSelectedSessionId(null);
    } finally {
      setLoading(false);
    }
  }, [canViewAllClasses, centerId]);

  React.useEffect(() => {
    void loadSessions();
  }, [loadSessions]);

  React.useEffect(() => {
    let cancelled = false;

    const loadReportCounts = async () => {
      if (sessions.length === 0) {
        setReportCountBySessionId({});
        return;
      }
      try {
        const counts = await reportService.getSessionCounts(
          sessions.map((session) => session.id)
        );
        if (cancelled) return;
        setReportCountBySessionId(counts);
      } catch {
        if (cancelled) return;
        setReportCountBySessionId({});
      }
    };

    void loadReportCounts();

    return () => {
      cancelled = true;
    };
  }, [sessions]);

  const classOptions = React.useMemo(
    () =>
      Array.from(
        new Map(
          sessions
            .filter((session) => session.class_id || session.class_name)
            .map(
              (session) =>
                [
                  session.class_id ?? session.class_name ?? session.title,
                  session.class_name ?? session.title,
                ] as const
            )
        ).entries()
      ).map(([value, label]) => ({ value, label })),
    [sessions]
  );

  const filteredSessions = React.useMemo(() => {
    const keyword = search.trim().toLowerCase();

    return sessions.filter((session) => {
      if (classFilter !== "ALL") {
        const sessionClassKey =
          session.class_id ?? session.class_name ?? session.title;
        if (sessionClassKey !== classFilter) {
          return false;
        }
      }

      if (!session.start_time) return false;
      const sessionDate = new Date(session.start_time);
      if (
        Number.isNaN(sessionDate.getTime()) ||
        sessionDate < weekStart ||
        sessionDate > weekEnd
      ) {
        return false;
      }

      if (!keyword) return true;

      const haystack = [
        session.title,
        session.class_name,
        session.status,
        session.session_type,
        formatSessionDateLabel(session.start_time, t, locale),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(keyword);
    });
  }, [classFilter, weekStart, weekEnd, search, sessions, t, locale]);

  React.useEffect(() => {
    if (filteredSessions.length === 0) return;
    if (
      selectedSessionId &&
      filteredSessions.some((session) => session.id === selectedSessionId)
    ) {
      return;
    }
    setSelectedSessionId(filteredSessions[0].id);
  }, [filteredSessions, selectedSessionId]);

  const selectedSession = React.useMemo(
    () => sessions.find((session) => session.id === selectedSessionId) ?? null,
    [selectedSessionId, sessions]
  );

  React.useEffect(() => {
    setSelectedSessionReports([]);
  }, [selectedSessionId]);

  const staffSubmissionStatus = React.useMemo(() => {
    if (!selectedSession) return [];
    const submittedTrainerIds = new Set(
      selectedSessionReports.map((report) => report.trainer_id)
    );
    return selectedSession.participants
      .filter(isReportRequiredParticipant)
      .map((participant) => ({
        participant,
        hasSubmitted: submittedTrainerIds.has(participant.user_id),
      }));
  }, [selectedSession, selectedSessionReports]);

  const submittedStaffCount = staffSubmissionStatus.filter(
    (item) => item.hasSubmitted
  ).length;

  return (
    <div className="mt-3 space-y-4">
      <Card extra="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-ink">
              {t("reportsBoard.title")}
            </h2>
            <p className="mt-1 text-sm text-ink-muted">
              {t("reportsBoard.subtitle")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ActionButton
              variant="secondary"
              onClick={() => navigate("/admin/report-templates/manage")}
              startIcon={<MdTune className="h-4 w-4" />}
            >
              {t("reportsBoard.manageTemplates")}
            </ActionButton>
            <ActionButton
              variant="primary"
              onClick={() => void loadSessions()}
              startIcon={<MdRefresh className="h-4 w-4" />}
            >
              {t("reportsBoard.refresh")}
            </ActionButton>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-stretch gap-3 border-t border-line pt-4">
          <label className="flex min-w-[220px] flex-1 items-center gap-2 rounded-xl border border-line px-3 py-2.5">
            <MdSearch className="h-4 w-4 shrink-0 text-ink-subtle" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("reportsBoard.searchPlaceholder")}
              className="bg-transparent w-full min-w-0 text-sm outline-none"
            />
          </label>

          <div className="flex shrink-0 items-center justify-between gap-2 rounded-xl border border-line px-2 py-2">
            <ActionButton
              variant="ghost"
              size="sm"
              onClick={() => setWeekOffset((value) => value - 1)}
              startIcon={<MdChevronLeft className="h-4 w-4" />}
            />
            <span className="flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-ink">
              <MdCalendarToday className="h-4 w-4 shrink-0 text-ink-subtle" />
              {t("sessionHub.weekLabel", {
                range: formatWeekRangeLabel(weekStart),
              })}
            </span>
            <ActionButton
              variant="ghost"
              size="sm"
              onClick={() => setWeekOffset((value) => value + 1)}
              startIcon={<MdChevronRight className="h-4 w-4" />}
            />
          </div>

          <label className="min-w-[180px] flex-1 rounded-xl border border-line px-3 py-1.5">
            <span className="block text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
              {t("reportsBoard.byClass")}
            </span>
            <select
              value={classFilter}
              onChange={(event) => setClassFilter(event.target.value)}
              className="bg-transparent w-full min-w-0 text-sm outline-none"
            >
              <option value="ALL">{t("sessionHub.allClasses")}</option>
              {classOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          {search || weekOffset !== 0 || classFilter !== "ALL" ? (
            <div className="flex shrink-0 items-center">
              <ActionButton
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearch("");
                  setWeekOffset(0);
                  setClassFilter("ALL");
                }}
                className="rounded-full"
                startIcon={<MdClose className="h-3.5 w-3.5" />}
              >
                {t("action.clearFilters")}
              </ActionButton>
            </div>
          ) : null}
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[380px,1fr]">
        <Card extra="p-4">
          <div className="mb-3 flex items-start justify-between gap-3">
            <div>
              <p className="flex items-center gap-2 text-sm font-semibold text-ink">
                <MdSchedule className="h-4 w-4 text-brand-500" />
                {t("reportsBoard.sessionList")}
              </p>
              <p className="mt-1 text-xs text-ink-muted">
                {t("reportsBoard.sessionListHint")}
              </p>
            </div>
            <span className="rounded-full bg-surface-muted px-2.5 py-1 text-xs font-semibold text-gray-600 dark:text-gray-300">
              {filteredSessions.length}
            </span>
          </div>

          <div className="max-h-[70vh] space-y-2 overflow-auto pr-1">
            {loading ? (
              <p className="py-8 text-center text-sm text-ink-subtle">
                {t("reportsBoard.loading")}
              </p>
            ) : null}
            {!loading && error ? (
              <p className="py-8 text-center text-sm text-red-500">
                {t(error)}
              </p>
            ) : null}
            {!loading && !error && filteredSessions.length === 0 ? (
              <p className="py-8 text-center text-sm text-ink-subtle">
                {t("reportsBoard.empty")}
              </p>
            ) : null}

            {filteredSessions.map((session) => {
              const isSelected = session.id === selectedSessionId;
              const statusLabel = t(statusBadgeKey(session.status));
              const sessionLocation = getSessionLocationLabel(session, t);
              const reportCount = reportCountBySessionId[session.id] ?? 0;
              const expectedReportCount = session.participants.filter(
                isReportRequiredParticipant
              ).length;
              const courseLabel =
                session.class_name?.trim() || t("reportsBoard.unknownCourse");
              const sessionTitleParts = session.title
                .split("/")
                .map((part) => part.trim())
                .filter(Boolean);
              const sessionCode = sessionTitleParts[0] ?? session.title;
              const sessionTitleSuffix = sessionTitleParts.slice(1).join(" / ");

              return (
                <button
                  key={session.id}
                  type="button"
                  onClick={() => setSelectedSessionId(session.id)}
                  className={`w-full rounded-xl border px-3 py-2.5 text-left transition hover:border-brand-300 hover:bg-white dark:border-line dark:bg-surface-muted/30 dark:hover:bg-surface-hover ${ isSelected ? "border-brand-400 bg-brand-50/50"
                      : "border-line bg-gray-50/60"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-xs text-ink">
                        <span className="font-bold">{sessionCode}</span>
                        {sessionTitleSuffix ? (
                          <span className="font-medium">
                            {" "}
                            / {sessionTitleSuffix}
                          </span>
                        ) : null}
                      </p>
                      <p className="mt-1 truncate text-xs font-medium text-gray-600 dark:text-gray-300">
                        {courseLabel}
                      </p>
                      <p className="mt-1 inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600 dark:bg-surface-hover dark:text-gray-300">
                        {formatSessionTimeRange(
                          session.start_time,
                          session.end_time,
                          t,
                          locale
                        )}{" "}
                        {formatSessionDateCompact(session.start_time, t)}
                      </p>
                      <p className="mt-1 block truncate text-xs text-ink-muted">
                        {sessionLocation}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <StatusBadge
                        label={statusLabel}
                        tone={getSessionStatusTone(session.status)}
                        className="whitespace-nowrap px-2 py-0.5 text-[11px]"
                      />
                      {expectedReportCount > 0 ? (
                        <StatusBadge
                          label={t("reportsBoard.submittedCount", {
                            count: reportCount,
                            total: expectedReportCount,
                          })}
                          tone={
                            reportCount >= expectedReportCount
                              ? "success"
                              : "warning"
                          }
                          className="whitespace-nowrap px-2 py-0.5 text-[10px]"
                        />
                      ) : null}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </Card>

        <Card extra="p-4">
          {selectedSession ? (
            <div className="mb-3 rounded-xl border border-line bg-gray-50 px-3.5 py-2.5 dark:bg-surface-muted/50">
              <p className="text-sm font-semibold text-ink">
                {selectedSession.title}
              </p>
              <p className="text-xs text-ink-muted">
                {selectedSession.class_name ??
                  t("participantInsights.unknownClass")}{" "}
                · {formatDateTime(selectedSession.start_time, t, locale)}
              </p>

              {staffSubmissionStatus.length > 0 ? (
                <div className="mt-3 flex items-center gap-2 border-t border-line pt-3">
                  <p className="text-xs font-semibold text-ink">
                    {t("reportsBoard.submittedReports")}
                  </p>
                  <StatusBadge
                    label={`${submittedStaffCount}/${staffSubmissionStatus.length}`}
                    tone={
                      submittedStaffCount === staffSubmissionStatus.length
                        ? "success"
                        : "warning"
                    }
                    className="px-2 py-0.5 text-[11px]"
                  />
                </div>
              ) : null}
            </div>
          ) : null}

          {selectedSessionId ? (
            <ReportView
              sessionId={selectedSessionId}
              onReportsLoaded={handleSelectedSessionReportsLoaded}
            />
          ) : (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-ink-subtle">
              <MdArticle className="h-12 w-12 opacity-30" />
              <p className="text-sm">{t("reportsBoard.selectSession")}</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default SessionReportsBoard;
